import { useState } from "react";
import { Avatar, Badge, Box, Chip, CircularProgress, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import CheckIcon from "@mui/icons-material/Check";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PersonIcon from "@mui/icons-material/Person";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import BusinessIcon from "@mui/icons-material/Business";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import { useNavSignals } from "src/hooks/useNavSignals";
import { eligibleCompanies } from "src/utils/hallinta/companyRoles";
import { captureError } from "src/utils/errorReporting";
import { initials, menuModelFor } from "src/utils/nav/navState";
import MyDetailsDialog from "src/components/MyDetailsDialog";
import ImpersonateDialog from "src/components/ImpersonateDialog";
import GlassDialog from "src/components/common/GlassDialog";

const ICONS = { list: FormatListBulletedIcon, requestQuote: RequestQuoteIcon, settings: SettingsIcon, logout: LogoutIcon, supportAgent: SupportAgentIcon, person: PersonIcon, switchAccount: SwitchAccountIcon };

// Avatar-triggered account menu. All item ordering/visibility comes from the
// pure menuModelFor(); this component is dumb rendering + close-on-select. The
// one stateful bit is the active-company switch (operators with >1 company): the
// menu shows a single collapsed "asiointirooli" row, and tapping it opens a
// picker modal. switchCompany re-mints the JWT, so we spin on the row mid-switch.
export default function AccountMenu({ fg }) {
  const { user, logout, switchCompany, ownerAsiakasId, canImpersonate, isImpersonating } = useAuth();
  const { role, hasSentRequests, hasActiveOrders, customerCounts, providerCounts } = useNavSignals();
  const t = useT();
  const [anchorEl, setAnchorEl] = useState(null);
  const [switchingId, setSwitchingId] = useState(null);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const open = Boolean(anchorEl);

  const person = user?.person?.[0] || null;
  const companies = eligibleCompanies(user);
  const supportUnread = providerCounts?.supportUnread ?? 0;
  const model = menuModelFor({ role, hasSentRequests, person, customerCounts, companies, activeAsiakasId: ownerAsiakasId, supportUnread, canImpersonate, isImpersonating, t });
  const showDot = (role === "customer" && hasActiveOrders) || supportUnread > 0;

  const close = () => setAnchorEl(null);
  const onAction = (item) => {
    close();
    if (item.action === "logout") logout();
    if (item.action === "profile") setProfileOpen(true);
    if (item.action === "impersonate") setImpersonateOpen(true);
  };

  // Picking from the modal: no-op (just close) when already active, otherwise
  // re-mint the JWT and close the modal once done.
  const onPickCompany = async (asiakasId) => {
    if (asiakasId === ownerAsiakasId) { setSwitchOpen(false); return; }
    setSwitchingId(asiakasId);
    try {
      await switchCompany(asiakasId);
      setSwitchOpen(false);
    } catch (err) {
      captureError(err, { tags: { entity: "company-selection", operation: "switch" } });
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        aria-label={t("auth.account.menuAriaLabel")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        sx={{ color: fg, borderRadius: 2 }}
      >
        <Badge variant="dot" color="error" invisible={!showDot} overlap="circular">
          <Avatar sx={{ width: 32, height: 32, bgcolor: "background.default", color: fg, fontSize: 14, fontWeight: 700, border: "1.5px solid", borderColor: "primary.main" }}>
            {initials(person)}
          </Avatar>
        </Badge>
        <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.25, color: fg }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 1 } } }}
      >
        {model.map((item, i) => {
          if (item.type === "header") {
            return (
              <Box key="header" sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" noWrap>{item.name}</Typography>
                {item.email && (
                  <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>
                    {item.email}
                  </Typography>
                )}
                {item.company && (
                  <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>
                    {t("auth.account.companyPrefix", { company: item.company })}
                  </Typography>
                )}
              </Box>
            );
          }
          if (item.type === "companySwitch") {
            // Collapsed active-company row: whole row opens the picker modal.
            return (
              <MenuItem key={item.key} onClick={() => { close(); setSwitchOpen(true); }} sx={{ alignItems: "flex-start" }}>
                <ListItemIcon sx={{ mt: 0.5 }}><BusinessIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={item.activeName} secondary={item.label} />
                <SwapHorizIcon fontSize="small" sx={{ ml: 1, alignSelf: "center", color: "text.secondary" }} />
              </MenuItem>
            );
          }
          if (item.type === "divider") {
            return <Divider key={`divider-${i}`} />;
          }
          const Icon = ICONS[item.icon];
          if (item.type === "link") {
            return (
              <MenuItem key={item.key} component={RouterLink} to={item.to} onClick={close}>
                {Icon && <ListItemIcon><Icon fontSize="small" /></ListItemIcon>}
                <ListItemText>{item.label}</ListItemText>
                {item.count ? <Chip size="small" color="primary" label={item.count} sx={{ ml: 1 }} /> : null}
              </MenuItem>
            );
          }
          return (
            <MenuItem key={item.key} onClick={() => onAction(item)}>
              {Icon && <ListItemIcon><Icon fontSize="small" /></ListItemIcon>}
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>

      <GlassDialog open={switchOpen} onClose={() => setSwitchOpen(false)} busy={switchingId !== null} title={t("common.nav.switchCompany")}>
        <List sx={{ py: 0 }}>
          {companies.map((c) => {
            const busy = switchingId === c.asiakasId;
            const active = c.asiakasId === ownerAsiakasId;
            return (
              <ListItemButton key={c.asiakasId} selected={active} disabled={switchingId !== null} onClick={() => onPickCompany(c.asiakasId)} sx={{ borderRadius: 1 }}>
                <ListItemIcon>
                  {busy ? <CircularProgress size={18} /> : active ? <CheckIcon fontSize="small" /> : null}
                </ListItemIcon>
                <ListItemText>{c.asiakasNimi}</ListItemText>
                {/* Mark Jerry-enabled providers (HAS_JERRY setting, from the auth
                    payload) so the user knows which switch lands in the operator
                    view rather than a plain-customer 403. */}
                {c.hasJerry && (
                  <Chip size="small" color="primary" label="Jerry" sx={{ ml: 1 }} />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </GlassDialog>

      {profileOpen && <MyDetailsDialog onClose={() => setProfileOpen(false)} />}
      {impersonateOpen && <ImpersonateDialog open onClose={() => setImpersonateOpen(false)} />}
    </>
  );
}
